import React, { useState, useEffect, useCallback } from 'react';
import { aiService } from '../services/AIService';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: string;
  price_cents: number;
}

interface CreditPacksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete: () => void;
  currentBalance: number;
}

const CreditPacksModal: React.FC<CreditPacksModalProps> = ({ isOpen, onClose, onPurchaseComplete, currentBalance }) => {
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      aiService.getCreditPacks().then(result => {
        if (result.success) {
          setPacks(result.packs);
        }
        setLoading(false);
      });
    }
  }, [isOpen]);

  // Listen for purchase completion messages from popup
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'flow2-purchase-complete') {
        onPurchaseComplete();
        onClose();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onPurchaseComplete, onClose]);

  const handlePurchase = useCallback(async (packId: string) => {
    setPurchasing(packId);
    setError(null);

    const result = await aiService.purchaseCredits(packId);
    if (result.success && result.checkout_url) {
      // Open Recurly hosted payment page in new window
      const popup = window.open(result.checkout_url, 'recurly-checkout', 'width=500,height=700,scrollbars=yes');
      if (!popup) {
        // Popup blocked - redirect instead
        window.location.href = result.checkout_url;
      }
    } else {
      setError(result.error || 'Failed to initiate purchase');
    }
    setPurchasing(null);
  }, []);

  if (!isOpen) return null;

  const getBestValue = (pack: CreditPack) => {
    const perCredit = pack.price_cents / pack.credits;
    if (pack.id === 'power') return 'Best Value';
    if (pack.id === 'pro') return 'Popular';
    return null;
  };

  return (
    <div className="credit-packs-overlay" onClick={onClose}>
      <div className="credit-packs-modal" onClick={e => e.stopPropagation()}>
        <button className="credit-packs-close" onClick={onClose}>×</button>
        <h2 className="credit-packs-title">Get More Credits</h2>
        <p className="credit-packs-subtitle">
          Current balance: <strong>{currentBalance} credits</strong>
        </p>

        {loading ? (
          <div className="credit-packs-loading">Loading...</div>
        ) : (
          <div className="credit-packs-grid">
            {packs.map(pack => {
              const badge = getBestValue(pack);
              const perCredit = (pack.price_cents / 100 / pack.credits).toFixed(3);
              return (
                <div key={pack.id} className={`credit-pack-card ${pack.id === 'pro' ? 'credit-pack-featured' : ''}`}>
                  {badge && <div className="credit-pack-badge">{badge}</div>}
                  <div className="credit-pack-credits">{pack.credits}</div>
                  <div className="credit-pack-label">credits</div>
                  <div className="credit-pack-price">{pack.price}</div>
                  <div className="credit-pack-per">${perCredit}/credit</div>
                  <button
                    className="credit-pack-buy"
                    onClick={() => handlePurchase(pack.id)}
                    disabled={purchasing !== null}
                  >
                    {purchasing === pack.id ? 'Opening checkout...' : 'Buy Now'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {error && <div className="credit-packs-error">{error}</div>}
      </div>
    </div>
  );
};

export default CreditPacksModal;
